import { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!res.ok) throw new Error("unauth");
      setUser(await res.json());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — let AuthCallback exchange session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      console.warn("Logout request failed:", e);
    }
    setUser(null);
  };

  // Re-validate the session every minute; when the server has ended it (hard 30-min lifetime), clear local state and ask for a fresh verified sign-in
  useEffect(() => {
    if (!user) return;
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (res.status === 401) {
          setUser(null);
          try { sessionStorage.clear(); Object.keys(localStorage).filter((k) => /^(gf_(cache|draft)|vidura_)/.test(k)).forEach((k) => localStorage.removeItem(k)); } catch { /* ignore */ }
          if (/\/(teamportal|clientportal|admin)/.test(window.location.pathname)) window.location.href = window.gfPath("/login?expired=1");
        }
      } catch { /* network blip — keep state */ }
    }, 60 * 1000);
    return () => clearInterval(iv);
  }, [user]);

  // 30-min inactivity auto-logout (mirrors server-side sliding session expiry)
  useEffect(() => {
    if (!user) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evs = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => {
      if (Date.now() - last > 30 * 60 * 1000) {
        logout();
        window.location.href = window.gfPath("/login?idle=1");
      }
    }, 60 * 1000);
    return () => { clearInterval(iv); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ user, loading, setUser, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
